/**
 * ============================================================================
 * SeaCloud Fast Agent Chat Completions API
 * ============================================================================
 *
 * This module provides a comprehensive API for interacting with SeaCloud's
 * Fast Agent system, which supports:
 *
 * - Multi-turn conversations with session management
 * - Tool calling for artifact generation (images, videos, music, etc.)
 * - Streaming and non-streaming response modes
 * - Multiple content types (text, image, video, audio, file)
 * - Flexible model selection (Claude, GPT, Gemini, etc.)
 *
 * IMPORTANT NOTES:
 * ================
 *
 * 1. REQUIRED PARAMETERS:
 *    - agent_id: Caller-provided agent identifier
 *    - messages: Array of conversation messages
 *
 * 2. TIMEOUT CONFIGURATION:
 *    - Default timeout: 30 seconds (may be too short for tool calls)
 *    - Recommended for agent operations: 120 seconds or more
 *    - Set via initSeacloud({ timeout: 120000 })
 *
 * 3. STREAMING MODE:
 *    - API always returns Server-Sent Events (SSE) format
 *    - params.stream controls how SDK consumes the response
 *    - For tool calls (image/video generation), streaming is RECOMMENDED
 *
 * 4. AUTHENTICATION:
 *    - Requires 'X-Project' header (configured via initSeacloud({ xProject: 'SeaVerse' }))
 *    - Uses Bearer token authentication
 *
 * 5. SESSION MANAGEMENT:
 *    - Use session_id to maintain multi-turn conversations
 *    - Increment seq for each message in the session
 *
 * @see AGENT_API.md for detailed documentation and examples
 */

import { getClient } from '../core/global-config.js';
import { SeacloudError } from '../core/types.js';
import { getApiToken, checkAndNotifyInsufficientBalance, checkResponseBodyForInsufficientBalance, checkStreamChunkForInsufficientBalance } from '../core/config.js';

/**
 * Message content type
 */
export type AgentMessageContentType = 'text' | 'image' | 'video' | 'audio' | 'file';

/**
 * Base message content
 */
interface BaseMessageContent {
  type: AgentMessageContentType;
}

/**
 * Text message content
 */
interface TextMessageContent extends BaseMessageContent {
  type: 'text';
  text: string;
}

/**
 * Image message content
 */
interface ImageMessageContent extends BaseMessageContent {
  type: 'image';
  image_url: string;
}

/**
 * Video message content
 */
interface VideoMessageContent extends BaseMessageContent {
  type: 'video';
  video_url: string;
}

/**
 * Audio message content
 */
interface AudioMessageContent extends BaseMessageContent {
  type: 'audio';
  audio_url: string;
}

/**
 * File message content
 */
interface FileMessageContent extends BaseMessageContent {
  type: 'file';
  file_url: string;
}

/**
 * Message content item (union type for type safety)
 */
export type AgentMessageContent =
  | TextMessageContent
  | ImageMessageContent
  | VideoMessageContent
  | AudioMessageContent
  | FileMessageContent;

/**
 * Message role type
 */
export type AgentMessageRole = 'developer' | 'user' | 'assistant' | 'tool';

/**
 * Chat message
 */
export interface AgentMessage {
  /**
   * Message role
   * - developer: System instructions/prompts (equivalent to "system" role)
   * - user: User input messages
   * - assistant: AI assistant responses (for multi-turn conversations)
   * - tool: Tool execution results
   */
  role: AgentMessageRole;

  /**
   * Message content array
   */
  content: AgentMessageContent[];

  /**
   * Tool call ID (for role: 'tool')
   */
  tool_call_id?: string;

  /**
   * Tool name (for role: 'tool')
   */
  name?: string;
}

/**
 * Tool function definition
 */
export interface AgentToolFunction {
  /**
   * Tool name
   * Available tools:
   *
   * Image Generation:
   * - seagen_text2image_flux1d_artifact_tool
   * - seagen_text2image_seedream40_artifact_tool
   * - seagen_text2image_google_gemini3_pro_image_artifact_tool
   * - seagen_blackforestlabs_flux_2_pro_tool
   * - mm_volces_seedream_4_5_gateway_tool
   *
   * Image Editing:
   * - seagen_edit_image_google_artifact_tool
   * - seagen_blackforestlabs_flux_2_pro_edit_tool
   *
   * Video Generation:
   * - seagen_image2video_wanx26_artifact_tool
   * - seagen_text2video_wanx26_artifact_tool
   * - seagen_image2video_seedance_pro_fast_artifact_tool
   * - mm_text2video_kling_v2_6_gateway_tool
   * - mm_image2video_kling_v2_6_i2v_gateway_tool
   *
   * Music & Audio:
   * - seagen_text2song_mureka_artifact_tool
   * - seagen_text2lyrics_mureka_artifact_tool
   */
  name: string;

  /**
   * Tool description (optional)
   */
  description?: string;

  /**
   * Tool parameters schema (optional)
   */
  parameters?: Record<string, any>;
}

/**
 * Tool definition
 */
export interface AgentTool {
  /**
   * Tool type (always 'function')
   */
  type: 'function';

  /**
   * Function definition
   */
  function: AgentToolFunction;
}

/**
 * Agent chat completions request parameters
 */
export interface AgentChatCompletionsParams {
  /**
   * Agent ID to invoke
   * @example "seaverse_agent"
   * @example "seagen_agent"
   */
  agent_id: string;

  /**
   * Array of messages comprising the conversation
   */
  messages: AgentMessage[];

  /**
   * AI model to use for processing
   * @default "custom_openai/vertex-ai-claude-sonnet-4.5"
   * @example "gpt-4o"
   * @example "custom_openai/vertex-ai-claude-sonnet-4.5"
   */
  model?: string;

  /**
   * Sampling temperature for creativity control (0.0 to 1.0)
   * - 0.0: Focused and deterministic responses
   * - 1.0: Maximum creativity and randomness
   * @default 0.7
   */
  temperature?: number;

  /**
   * Session ID for multi-turn conversations
   * Used to maintain conversation context across multiple requests
   * Server returns this in the response; use it in subsequent calls
   */
  session_id?: string;

  /**
   * User identifier for tracking and analytics
   * Optional field for associating requests with specific users
   */
  user_id?: string;

  /**
   * Message ID for SSE reconnection (ADVANCED USE)
   * Only use this when resuming an interrupted streaming response
   * Server returns msg_id in response; use it to resume from disconnect point
   *
   * WARNING: Do NOT use for multi-turn conversations
   * For multi-turn, just include full message history + session_id
   *
   * @example
   * // DON'T do this for multi-turn:
   * // { msg_id: previousResponse.msg_id, seq: 1 }
   *
   * // DO this instead:
   * // { session_id: previousResponse.session_id, messages: [...allMessages] }
   */
  msg_id?: string;

  /**
   * Sequence number for message resumption (ADVANCED USE)
   * Only used with msg_id for resuming interrupted streams
   * For regular multi-turn conversations, omit this parameter
   *
   * @default 0
   */
  seq?: number;

  /**
   * Business-specific parameters
   * Key-value pairs passed through to backend systems
   * Common use cases:
   * - aspect_ratio: Video/image aspect ratio (e.g., "16:9", "1:1")
   * - duration: Video duration in seconds
   * - resolution: Output resolution (e.g., "1080p", "4k")
   */
  metadata?: Record<string, any>;

  /**
   * List of tools available to the agent
   * Agent automatically selects appropriate tool based on user request
   */
  tools?: AgentTool[];

  /**
   * Enable Server-Sent Events streaming
   * @default true
   */
  stream?: boolean;

  /**
   * Maximum number of tokens to generate
   */
  max_tokens?: number;

  /**
   * Number of completions to generate
   * @default 1
   */
  n?: number;

  /**
   * Sequences where the API will stop generating
   */
  stop?: string | string[];

  /**
   * Penalty for new tokens based on presence (-2.0 to 2.0)
   * @default 0
   */
  presence_penalty?: number;

  /**
   * Penalty for new tokens based on frequency (-2.0 to 2.0)
   * @default 0
   */
  frequency_penalty?: number;

  /**
   * Top-p nucleus sampling (0-1)
   */
  top_p?: number;

  /**
   * Request timeout in milliseconds
   * Overrides the global timeout set in initSeacloud()
   * @default 30000 (from global config)
   * @recommended 120000 or more for agent operations with tool calls
   * @example 120000 for 120 seconds timeout
   */
  timeout?: number;
}

/**
 * Artifact (generated file)
 */
export interface AgentArtifact {
  /**
   * File name
   * @example "image.webp"
   */
  name: string;

  /**
   * CDN URL to access the file
   */
  url: string;

  /**
   * Content type
   * @example "image" | "video" | "audio"
   */
  type: string;

  /**
   * MIME type
   * @example "image/webp" | "video/mp4" | "audio/mp3"
   */
  mime_type?: string;

  /**
   * File size in bytes
   */
  size?: number;

  /**
   * Duration in seconds (for video/audio)
   */
  duration?: number;

  /**
   * Width in pixels (for image/video)
   */
  width?: number;

  /**
   * Height in pixels (for image/video)
   */
  height?: number;

  /**
   * Additional metadata
   */
  [key: string]: any;
}

/**
 * Tool call information
 */
export interface AgentToolCall {
  /**
   * Tool call ID
   */
  id: string;

  /**
   * Tool type
   */
  type: 'function';

  /**
   * Function call details
   */
  function: {
    /**
     * Function name
     */
    name: string;

    /**
     * Function arguments (JSON string)
     */
    arguments: string;
  };

  /**
   * Tool call index
   */
  index?: number;
}

/**
 * Delta content for streaming chunks
 */
export interface AgentChatCompletionDelta {
  /**
   * Message role (only in first chunk)
   */
  role?: AgentMessageRole;

  /**
   * Content delta (string or array)
   */
  content?: string | AgentMessageContent[];

  /**
   * Artifacts (generated files)
   */
  artifacts?: AgentArtifact[];

  /**
   * Tool calls
   */
  tool_calls?: AgentToolCall[];

  /**
   * Refusal message
   */
  refusal?: string | null;

  /**
   * Function call (deprecated, use tool_calls)
   */
  function_call?: any;

  /**
   * Audio content
   */
  audio?: any;

  /**
   * Additional provider-specific fields
   */
  provider_specific_fields?: any;
}

/**
 * Content filter result
 */
export interface AgentContentFilterResult {
  /**
   * Error information
   */
  error?: {
    /**
     * Error code
     */
    code: string;

    /**
     * Error message
     */
    message: string;
  };

  /**
   * Additional filter results
   */
  [key: string]: any;
}

/**
 * Chat completion choice (streaming)
 */
export interface AgentChatCompletionChunkChoice {
  /**
   * Choice index
   */
  index: number;

  /**
   * Delta message for this chunk
   */
  delta: AgentChatCompletionDelta;

  /**
   * Finish reason (only in last chunk)
   * - "stop": Natural completion
   * - "length": Maximum token limit reached
   * - "tool_calls": Tool execution triggered
   * - "content_filter": Content filtered
   */
  finish_reason: string | null;

  /**
   * Log probabilities
   */
  logprobs?: any;

  /**
   * Content filter results
   */
  content_filter_results?: AgentContentFilterResult;

  /**
   * Content filter result (singular)
   */
  content_filter_result?: AgentContentFilterResult;
}

/**
 * Usage statistics
 */
export interface AgentUsage {
  /**
   * Prompt tokens
   */
  prompt_tokens: number;

  /**
   * Completion tokens
   */
  completion_tokens: number;

  /**
   * Total tokens
   */
  total_tokens: number;

  /**
   * Completion tokens details
   */
  completion_tokens_details?: {
    /**
     * Accepted prediction tokens
     */
    accepted_prediction_tokens?: number;

    /**
     * Audio tokens
     */
    audio_tokens?: number;

    /**
     * Reasoning tokens
     */
    reasoning_tokens?: number;

    /**
     * Rejected prediction tokens
     */
    rejected_prediction_tokens?: number;
  };

  /**
   * Prompt tokens details
   */
  prompt_tokens_details?: {
    /**
     * Audio tokens
     */
    audio_tokens?: number;

    /**
     * Cached tokens
     */
    cached_tokens?: number;

    /**
     * Text tokens
     */
    text_tokens?: number | null;

    /**
     * Image tokens
     */
    image_tokens?: number | null;
  };

  /**
   * Cost in USD
   */
  cost?: number;
}

/**
 * Chat completion streaming chunk
 */
export interface AgentChatCompletionChunk {
  /**
   * Unique identifier for the completion
   */
  id: string;

  /**
   * Object type (always "chat.completion.chunk")
   */
  object: string;

  /**
   * Unix timestamp
   */
  created: number;

  /**
   * Model used
   */
  model: string;

  /**
   * System fingerprint
   */
  system_fingerprint?: string;

  /**
   * List of choices
   */
  choices: AgentChatCompletionChunkChoice[];

  /**
   * Usage statistics (usually in last chunk)
   */
  usage?: AgentUsage;

  /**
   * Session ID for conversation continuation
   */
  session_id?: string;

  /**
   * Message ID
   */
  msg_id?: string;

  /**
   * Provider-specific fields
   */
  provider_specific_fields?: any;

  /**
   * Citations
   */
  citations?: any;

  /**
   * Service tier
   */
  service_tier?: string | null;

  /**
   * Obfuscation
   */
  obfuscation?: string;
}

/**
 * Message in non-streaming response
 */
export interface AgentResponseMessage {
  /**
   * Message role
   */
  role: AgentMessageRole;

  /**
   * Message content
   */
  content: string;

  /**
   * Tool calls
   */
  tool_calls?: AgentToolCall[];

  /**
   * Refusal message
   */
  refusal?: string | null;

  /**
   * Function call (deprecated)
   */
  function_call?: any;

  /**
   * Audio content
   */
  audio?: any;
}

/**
 * Chat completion choice (non-streaming)
 */
export interface AgentChatCompletionChoice {
  /**
   * Choice index
   */
  index: number;

  /**
   * Message
   */
  message: AgentResponseMessage;

  /**
   * Finish reason
   */
  finish_reason: string | null;

  /**
   * Log probabilities
   */
  logprobs?: any;

  /**
   * Content filter results
   */
  content_filter_results?: AgentContentFilterResult;
}

/**
 * Chat completion response (non-streaming)
 */
export interface AgentChatCompletionResponse {
  /**
   * Unique identifier
   */
  id: string;

  /**
   * Object type (always "chat.completion")
   */
  object: string;

  /**
   * Unix timestamp
   */
  created: number;

  /**
   * Model used
   */
  model: string;

  /**
   * System fingerprint
   */
  system_fingerprint?: string;

  /**
   * List of choices
   */
  choices: AgentChatCompletionChoice[];

  /**
   * Usage statistics
   */
  usage?: AgentUsage;

  /**
   * Session ID
   */
  session_id?: string;

  /**
   * Message ID
   */
  msg_id?: string;

  /**
   * Provider-specific fields
   */
  provider_specific_fields?: any;

  /**
   * Service tier
   */
  service_tier?: string | null;

  /**
   * Generated artifacts
   */
  artifacts?: AgentArtifact[];
}

/**
 * Call SeaCloud Fast Agent API (non-streaming)
 *
 * @example
 * ```typescript
 * import { initSeacloud, agentChatCompletions } from 'seacloud-sdk';
 *
 * initSeacloud({
 *   apiKey: 'your-api-key',
 *   baseUrl: 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai',
 * });
 *
 * // Simple chat
 * const response = await agentChatCompletions({
 *   agent_id: 'seaverse_agent',
 *   messages: [
 *     {
 *       role: 'user',
 *       content: [{ type: 'text', text: 'Hello!' }]
 *     }
 *   ],
 *   model: 'gpt-4o',
 * });
 *
 * console.log(response.choices[0].message.content);
 * ```
 *
 * @example
 * ```typescript
 * // Generate an image
 * const response = await agentChatCompletions({
 *   agent_id: 'seaverse_agent',
 *   messages: [
 *     {
 *       role: 'user',
 *       content: [{ type: 'text', text: 'Generate a cute puppy photo' }]
 *     }
 *   ],
 *   model: 'gpt-4o',
 *   tools: [
 *     {
 *       type: 'function',
 *       function: { name: 'seagen_text2image_flux1d_artifact_tool' }
 *     }
 *   ]
 * });
 *
 * // Access generated image
 * const imageUrl = response.artifacts?.[0]?.url;
 * ```
 *
 * @param params Request parameters
 * @returns Chat completion response
 */
export async function agentChatCompletions(
  params: AgentChatCompletionsParams & { stream?: false }
): Promise<AgentChatCompletionResponse>;

/**
 * Call SeaCloud Fast Agent API (streaming)
 *
 * @example
 * ```typescript
 * import { initSeacloud, agentChatCompletions } from 'seacloud-sdk';
 *
 * initSeacloud({
 *   apiKey: 'your-api-key',
 *   baseUrl: 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai',
 * });
 *
 * const stream = await agentChatCompletions({
 *   agent_id: 'seaverse_agent',
 *   messages: [
 *     {
 *       role: 'user',
 *       content: [{ type: 'text', text: 'Tell me a story' }]
 *     }
 *   ],
 *   model: 'gpt-4o',
 *   stream: true,
 * });
 *
 * for await (const chunk of stream) {
 *   const content = chunk.choices[0]?.delta?.content;
 *   if (typeof content === 'string') {
 *     process.stdout.write(content);
 *   }
 *
 *   // Handle artifacts
 *   const artifacts = chunk.choices[0]?.delta?.artifacts;
 *   if (artifacts) {
 *     console.log('Generated artifacts:', artifacts);
 *   }
 * }
 * ```
 *
 * @param params Request parameters with stream: true
 * @returns Async iterator of chat completion chunks
 */
export async function agentChatCompletions(
  params: AgentChatCompletionsParams & { stream: true }
): Promise<AsyncIterable<AgentChatCompletionChunk>>;

// Implementation
export async function agentChatCompletions(
  params: AgentChatCompletionsParams
): Promise<AgentChatCompletionResponse | AsyncIterable<AgentChatCompletionChunk>> {
  const client = await getClient();
  const config = client.getConfig();

  const url = `${config.baseUrl}/agent/api/v1/chat/completions`;

  // ============================================================================
  // PARAMETER PROCESSING LOGIC
  // ============================================================================

  /**
   * Default Model:
   * - If not specified, uses 'custom_openai/vertex-ai-claude-sonnet-4.5'
   * - This is Claude Sonnet 4.5, optimized for agent operations
   * - You can override with any supported model (gpt-4o, claude-4, etc.)
   */
  const model = params.model || 'custom_openai/vertex-ai-claude-sonnet-4.5';

  /**
   * Stream Mode:
   * - Agent API ALWAYS returns Server-Sent Events (SSE) format
   * - Even if params.stream=false, API returns SSE chunks
   * - We force stream: true in API request for consistency
   * - For non-streaming (params.stream=false), we collect all chunks internally
   */
  const userWantsStreaming = params.stream !== false; // Default to true
  const requestBody = {
    ...params,
    model,
    stream: true, // Always request SSE from API
  };

  // ============================================================================
  // TIMEOUT & REQUEST HANDLING
  // ============================================================================

  /**
   * Timeout Configuration:
   * - Default: 30 seconds (set in config)
   * - For agent operations (especially with tool calls like image generation):
   *   Recommend setting timeout to 120000ms (2 minutes) or more
   * - Can override per-request: agentChatCompletions({ ..., timeout: 120000 })
   * - Or set globally: initSeacloud({ apiKey: '...', timeout: 120000 })
   */
  // Use params.timeout if provided, otherwise use global config.timeout
  const timeoutMs = params.timeout ?? config.timeout;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    /**
     * Request Headers:
     * - Content-Type: application/json (standard)
     * - Authorization: Bearer token for authentication (dynamically fetched from localStorage/env)
     * - X-Project: 由 initSeacloud({ xProject: '...' }) 配置
     */
    // 每次请求时动态获取最新的 token（支持 localStorage 自动读取）
    const currentToken = await getApiToken(config.apiKey);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${currentToken}`,
    };

    // 仅在配置了 xProject 时添加 X-Project header
    if (config.xProject) {
      headers['X-Project'] = config.xProject;
    }

    const response = await config.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text();
      const err = new SeacloudError(
        `HTTP ${response.status}: ${errorBody}`,
        response.status,
        errorBody,
      );
      checkAndNotifyInsufficientBalance(err);
      throw err;
    }

    // ============================================================================
    // RESPONSE HANDLING
    // ============================================================================

    /**
     * Response Routing:
     * - If user wants streaming (params.stream !== false):
     *   Return async iterator that yields chunks as they arrive
     *
     * - If user wants non-streaming (params.stream === false):
     *   Internally collect all SSE chunks and return complete response
     *
     * Note: Both paths read SSE format from API, just different consumption patterns
     */
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const result = await response.json();
      checkResponseBodyForInsufficientBalance(result);
      return result as AgentChatCompletionResponse;
    }

    if (userWantsStreaming) {
      return parseAgentStreamingResponse(response);
    }

    // Non-streaming: Collect all chunks and return complete response
    return await parseAgentNonStreamingResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof SeacloudError) {
      checkAndNotifyInsufficientBalance(error);
      throw error;
    }

    if ((error as Error).name === 'AbortError') {
      throw new SeacloudError(`Request timeout after ${timeoutMs}ms`);
    }

    throw new SeacloudError(
      `Request failed: ${(error as Error).message}`,
      undefined,
      error,
    );
  }
}

/**
 * Parse non-streaming response (API returns SSE even with stream: false)
 */
async function parseAgentNonStreamingResponse(
  response: Response
): Promise<AgentChatCompletionResponse> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new SeacloudError('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let artifacts: AgentArtifact[] = [];
  let finishReason = '';
  let sessionId = '';
  let msgId = '';
  let lastChunk: AgentChatCompletionChunk | null = null;
  let usage: AgentUsage | undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (!trimmedLine) continue;
        if (trimmedLine === 'data: [DONE]') continue;
        if (trimmedLine === 'event: heartbeat') continue;

        if (trimmedLine.startsWith('data: ')) {
          const data = trimmedLine.slice(6).trim();

          try {
            const parsed = JSON.parse(data) as AgentChatCompletionChunk;
            checkStreamChunkForInsufficientBalance(parsed);
            lastChunk = parsed;

            const delta = parsed.choices?.[0]?.delta;

            if (delta?.content) {
              if (typeof delta.content === 'string') {
                fullContent += delta.content;
              } else if (Array.isArray(delta.content)) {
                for (const item of delta.content) {
                  if (item.type === 'text' && item.text) {
                    fullContent += item.text;
                  }
                }
              }
            }

            if (delta?.artifacts) {
              artifacts.push(...delta.artifacts);
            }

            if (parsed.choices?.[0]?.finish_reason) {
              finishReason = parsed.choices[0].finish_reason;
            }

            if (parsed.session_id) {
              sessionId = parsed.session_id;
            }

            if (parsed.msg_id) {
              msgId = parsed.msg_id;
            }

            if (parsed.usage) {
              usage = parsed.usage;
            }
          } catch (e) {
            // Skip invalid JSON
            console.warn('Failed to parse SSE chunk:', data.substring(0, 100));
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!lastChunk) {
    throw new SeacloudError('No valid response chunks received');
  }

  return {
    id: lastChunk.id,
    object: 'chat.completion',
    created: lastChunk.created,
    model: lastChunk.model,
    system_fingerprint: lastChunk.system_fingerprint,
    choices: [
      {
        index: 0,
        message: {
          role: 'assistant',
          content: fullContent,
        },
        finish_reason: finishReason || null,
      },
    ],
    usage,
    session_id: sessionId || undefined,
    msg_id: msgId || undefined,
    artifacts: artifacts.length > 0 ? artifacts : undefined,
  };
}

/**
 * Parse streaming response into async iterable
 */
async function* parseAgentStreamingResponse(
  response: Response
): AsyncIterable<AgentChatCompletionChunk> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new SeacloudError('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        // Skip empty lines and comments
        if (!trimmedLine || trimmedLine.startsWith(':')) continue;

        // Handle event type declarations (e.g., "event: heartbeat")
        if (trimmedLine.startsWith('event: ')) {
          const eventType = trimmedLine.slice(7).trim();
          if (eventType === 'heartbeat') {
            // Heartbeat events can be logged but don't yield
            continue;
          }
        }

        // Handle data events
        if (trimmedLine.startsWith('data: ')) {
          const data = trimmedLine.slice(6).trim();

          // Check for stream completion signal
          if (data === '[DONE]') {
            break;
          }

          try {
            const chunk = JSON.parse(data) as AgentChatCompletionChunk;
            checkStreamChunkForInsufficientBalance(chunk);
            yield chunk;
          } catch (error) {
            console.warn('Failed to parse SSE chunk:', data);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Helper function to create a text message
 *
 * @param role - Message role ('user' | 'assistant' | 'developer' | 'tool')
 * @param text - Text content
 * @returns Agent message with text content
 *
 * @example
 * ```typescript
 * const message = createTextMessage('user', 'Hello!');
 * ```
 */
export function createTextMessage(role: AgentMessageRole, text: string): AgentMessage {
  return {
    role,
    content: [{ type: 'text', text }],
  };
}

/**
 * Helper function to create a message with image
 *
 * @param role - Message role ('user' | 'assistant' | 'developer' | 'tool')
 * @param text - Text content
 * @param imageUrl - Image URL
 * @returns Agent message with text and image content
 *
 * @example
 * ```typescript
 * const message = createImageMessage('user', 'What is this?', 'https://example.com/image.jpg');
 * ```
 */
export function createImageMessage(role: AgentMessageRole, text: string, imageUrl: string): AgentMessage {
  return {
    role,
    content: [
      { type: 'text', text },
      { type: 'image', image_url: imageUrl },
    ],
  };
}

/**
 * Helper function to create a message with video
 *
 * @param role - Message role
 * @param text - Text content
 * @param videoUrl - Video URL
 * @returns Agent message with text and video content
 */
export function createVideoMessage(role: AgentMessageRole, text: string, videoUrl: string): AgentMessage {
  return {
    role,
    content: [
      { type: 'text', text },
      { type: 'video', video_url: videoUrl },
    ],
  };
}

/**
 * Helper function to create a message with audio
 *
 * @param role - Message role
 * @param text - Text content
 * @param audioUrl - Audio URL
 * @returns Agent message with text and audio content
 */
export function createAudioMessage(role: AgentMessageRole, text: string, audioUrl: string): AgentMessage {
  return {
    role,
    content: [
      { type: 'text', text },
      { type: 'audio', audio_url: audioUrl },
    ],
  };
}

/**
 * Helper function to create a tool
 */
export function createTool(toolName: string): AgentTool {
  return {
    type: 'function',
    function: {
      name: toolName,
    },
  };
}
